import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Space,
  Spinner,
  Text,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { GlobalApi } from '@Apis';
import { useOrgStore } from 'src/models/useOrgModel';

type Phase =
  | 'loading' // initial /status fetch in flight
  | 'pending' // not consented; idle
  | 'granting' // user clicked Grant; polling localStorage + /status
  | 'timeout' // polling exhausted; offer Re-check
  | 'granted' // consent JUST completed in this session — show ✓ banner once
  | 'silent' // already consented before mount (refresh / re-entry) — render nothing
  | 'error'; // /status fetch failed; fail-open hint

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 20; // 3s × 20 = 60s, matches design §五

// Same-origin localStorage key written by /admin-consent.html after AAD
// redirects back. Bumping this string requires changing the static HTML in
// lockstep — do a global search before renaming.
const OUTCOME_KEY = 'cas_smart_alert_consent_outcome';
const EXPECTED_KEY = 'cas_smart_alert_consent_expected';

// Per-tab + per-org dismiss flag for the warning banner. sessionStorage means
// the dismiss survives a navigation away and back inside this tab but is
// cleared when the user closes the tab / signs out — the next sign-in always
// sees the banner again, so a previous admin's dismiss does not silently hide
// the prerequisite from someone else. The orgId suffix scopes the dismiss to
// a single org: dismissing on org A must not silently hide the banner after
// the user switches to org B (which has its own consent state).
const DISMISS_KEY_PREFIX = 'cas_smart_alert_consent_banner_dismissed';

function dismissKey(orgId: string): string {
  return `${DISMISS_KEY_PREFIX}:${orgId}`;
}

function readDismissed(orgId: string): boolean {
  try {
    return sessionStorage.getItem(dismissKey(orgId)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(orgId: string) {
  try {
    sessionStorage.setItem(dismissKey(orgId), '1');
  } catch {
    // best-effort
  }
}

function clearDismissed(orgId: string) {
  try {
    sessionStorage.removeItem(dismissKey(orgId));
  } catch {
    // best-effort
  }
}

interface ExpectedConsent {
  tenantId: string;
  state: string;
  ts?: number;
}

type ConsentOutcome =
  | { status: 'approved'; tenant: string; state: string; ts?: number }
  | {
      status: 'rejected' | 'error';
      error?: string;
      errorDescription?: string;
      state?: string;
      ts?: number;
    };

function readOutcome(): ConsentOutcome | null {
  try {
    const raw = localStorage.getItem(OUTCOME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentOutcome;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !['approved', 'rejected', 'error'].includes(parsed.status)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function normalizeTenantId(tenantId: string) {
  return tenantId.trim().toLowerCase();
}

function readExpectedConsent(): ExpectedConsent | null {
  try {
    const raw = localStorage.getItem(EXPECTED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ExpectedConsent;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.tenantId ||
      !parsed.state
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
function clearOutcome() {
  try {
    localStorage.removeItem(OUTCOME_KEY);
  } catch {
    // localStorage unavailable (private mode / quota) — best-effort.
  }
}

function writeExpectedConsent(tenantId: string, state: string) {
  try {
    localStorage.setItem(
      EXPECTED_KEY,
      JSON.stringify({ tenantId, state, ts: Date.now() })
    );
  } catch {
    // localStorage unavailable (private mode / quota) ? best-effort.
  }
}

function clearExpectedConsent() {
  try {
    localStorage.removeItem(EXPECTED_KEY);
  } catch {
    // localStorage unavailable (private mode / quota) ? best-effort.
  }
}

/**
 * Admin Consent banner for the Misdirected Email Detection page.
 *
 * Flow:
 *   1. mount → clear any stale localStorage outcome → GET /status.
 *   2. if consented → static "granted" alert; stop.
 *   3. else → Warning alert + Grant button.
 *   4. user clicks Grant -> warning modal -> Continue -> GET /url -> window.open(url, '_blank') -> poll loop.
 *   5. each poll tick (every 3s, up to 20 attempts):
 *      a. read same-origin localStorage outcome written by /admin-consent.html
 *         - approved → POST /ack(orgId) → toast + flip to granted (stop polling)
 *         - rejected → toast (cancelled) + back to pending (stop polling)
 *         - error    → toast (with error code) + back to pending (stop polling)
 *      b. fallback: GET /status — if consented, flip to granted (stop).
 *      c. else: continue. After 20 attempts → 'timeout', offer Re-check.
 *
 * Failure stance is fail-open: a 5xx on /status flips the banner to a passive
 * "Status check unavailable" hint and does NOT block the toggle below — the
 * detection feature itself doesn't require this banner to be functional.
 */
const AdminConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const { orgId, getOrgName } = useOrgStore();

  const [phase, setPhase] = useState<Phase>('loading');
  const [isGrantWarningOpen, setIsGrantWarningOpen] = useState(false);
  // Parent re-mounts this component with `key={orgId}` on org switch, so the
  // initial dismissed read also picks up the new org's flag automatically.
  const [dismissed, setDismissed] = useState<boolean>(() =>
    readDismissed(orgId)
  );
  const pollAttemptRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const handleDismiss = useCallback(() => {
    writeDismissed(orgId);
    setDismissed(true);
  }, [orgId]);

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const fetchStatusOnce = useCallback(async (): Promise<boolean | null> => {
    try {
      const res = await GlobalApi.getSmartAlertAdminConsentStatus();
      return !!res.data?.consented;
    } catch {
      return null;
    }
  }, []);

  // Mount-time: defensive clear of stale outcome + initial status fetch.
  // If the API already says consented, fall into `silent` (render nothing) —
  // a permanent ✓ banner on every page load adds no signal. Only the live
  // grant flow (finishGranted) flips to `granted`, which surfaces the ✓
  // confirmation strip exactly once for the session.
  useEffect(() => {
    cancelledRef.current = false;
    clearOutcome();
    clearExpectedConsent();
    (async () => {
      const consented = await fetchStatusOnce();
      if (cancelledRef.current) return;
      if (consented === null) setPhase('error');
      else setPhase(consented ? 'silent' : 'pending');
    })();
    return () => {
      cancelledRef.current = true;
      clearPollTimer();
    };
  }, [fetchStatusOnce]);

  const finishGranted = useCallback(() => {
    setPhase('granted');
    window.showNotification?.({
      titleMsg: t(
        'globalSetting.misdirectedEmailDetection.consentBanner.toastConsented'
      ),
      severity: 'success',
    });
  }, [t]);

  const startPolling = useCallback(() => {
    pollAttemptRef.current = 0;
    const tick = async () => {
      if (cancelledRef.current) return;
      pollAttemptRef.current += 1;

      // (a) Same-origin bridge: did /admin-consent.html drop an outcome?
      const outcome = readOutcome();
      if (outcome) {
        const expectedConsent = readExpectedConsent();
        clearOutcome(); // single-shot; never act on the same entry twice.

        if (outcome.status === 'approved') {
          if (
            !expectedConsent ||
            normalizeTenantId(outcome.tenant) !==
              normalizeTenantId(expectedConsent.tenantId) ||
            outcome.state !== expectedConsent.state
          ) {
            clearExpectedConsent();
            setPhase('pending');
            window.showNotification?.({
              titleMsg: t(
                'globalSetting.misdirectedEmailDetection.consentBanner.toastError'
              ),
              severity: 'error',
            });
            return;
          }
          clearExpectedConsent();
          // Banner re-checks the expected tenant/state before /ack; BFF repeats
          // the same validation with signed one-time state before recording.
          try {
            await GlobalApi.postSmartAlertAdminConsentAck({
              orgId,
              tenantId: outcome.tenant,
              state: outcome.state,
            });
          } catch {
            if (cancelledRef.current) return;
            setPhase('pending');
            window.showNotification?.({
              titleMsg: t(
                'globalSetting.misdirectedEmailDetection.consentBanner.errorAck'
              ),
              severity: 'error',
            });
            return;
          }
          if (cancelledRef.current) return;
          finishGranted();
          return;
        }

        clearExpectedConsent();

        if (outcome.status === 'rejected') {
          if (cancelledRef.current) return;
          setPhase('pending');
          window.showNotification?.({
            titleMsg: t(
              'globalSetting.misdirectedEmailDetection.consentBanner.toastRejected'
            ),
            severity: 'warning',
          });
          return;
        }

        // outcome.status === 'error'
        if (cancelledRef.current) return;
        setPhase('pending');
        window.showNotification?.({
          titleMsg: t(
            'globalSetting.misdirectedEmailDetection.consentBanner.toastError'
          ),
          severity: 'error',
        });
        return;
      }

      // (b) Fallback: server-side /status. Covers the case where the admin
      //     closed the redirect tab before localStorage was written but the
      //     consent itself was already recorded server-side via some other
      //     path, OR where localStorage is unavailable (private mode).
      const consented = await fetchStatusOnce();
      if (cancelledRef.current) return;
      if (consented === true) {
        finishGranted();
        return;
      }

      // (c) Continue polling until the cap.
      if (pollAttemptRef.current >= POLL_MAX_ATTEMPTS) {
        setPhase('timeout');
        return;
      }
      pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  }, [fetchStatusOnce, finishGranted, orgId, t]);

  const openGrantWarning = useCallback(() => {
    setIsGrantWarningOpen(true);
  }, []);

  const closeGrantWarning = useCallback(() => {
    setIsGrantWarningOpen(false);
  }, []);

  const handleConfirmGrant = useCallback(async () => {
    setIsGrantWarningOpen(false);
    // Clicking Continue means the admin actively wants to complete this step
    // right now — any earlier dismiss is stale and must not silently hide the
    // banner if the consent flow is rejected / the redirect tab is closed and
    // the page is later refreshed. Clear the per-org dismiss before kicking
    // off the polling loop so the banner always re-surfaces on /status:false.
    clearDismissed(orgId);
    setDismissed(false);
    setPhase('granting');
    try {
      const res = await GlobalApi.getSmartAlertAdminConsentUrl();
      const url = res.data?.url;
      const tenantId = res.data?.tenantId;
      const state = res.data?.state;
      if (!url || !tenantId || !state) throw new Error('empty url');
      writeExpectedConsent(tenantId, state);
      // Pop the AAD adminconsent URL in a new tab. Use noopener so the new tab
      // can't reach back into our window via window.opener.
      window.open(url, '_blank', 'noopener,noreferrer');
      startPolling();
    } catch {
      // Surface a passive error hint; user can click Grant again.
      setPhase('pending');
      window.showNotification?.({
        titleMsg: t(
          'globalSetting.misdirectedEmailDetection.consentBanner.errorFetchUrl'
        ),
        severity: 'error',
      });
    }
  }, [orgId, startPolling, t]);

  const handleRecheck = useCallback(() => {
    setPhase('granting');
    startPolling();
  }, [startPolling]);

  if (phase === 'loading') {
    return (
      <Alert severity="warning" variant="outline" mt="4x">
        <Spinner size="sm" />
      </Alert>
    );
  }

  // 'granted' = consent JUST completed in this mount → show ✓ confirmation
  // strip once. On the next page-load / refresh the API returns consented
  // immediately, the mount effect routes to 'silent' instead, and we render
  // nothing — avoids a permanent green bar on every visit. Honor `granted`
  // even after a session dismiss so the user gets visual confirmation that
  // their click worked even if they had hidden the warning earlier.
  if (phase === 'granted') {
    return (
      <Alert severity="success" variant="outline" mt="4x">
        <Text>
          {t(
            'globalSetting.misdirectedEmailDetection.consentBanner.grantedHint'
          )}
        </Text>
      </Alert>
    );
  }

  if (phase === 'silent') {
    return null;
  }

  // From here on, all remaining phases (`error` / `pending` / `granting` /
  // `timeout`) are dismissible for the rest of the session. The dismiss flag
  // lives in sessionStorage, so it clears on tab close / sign-out and the
  // banner returns on the next sign-in — a previous admin's dismiss never
  // silently hides the prerequisite from someone else.
  if (dismissed) {
    return null;
  }

  if (phase === 'error') {
    return (
      <Alert
        severity="warning"
        variant="outline"
        mt="4x"
        isClosable
        onClose={handleDismiss}
      >
        <Text>
          {t(
            'globalSetting.misdirectedEmailDetection.consentBanner.errorFetchStatus'
          )}
        </Text>
      </Alert>
    );
  }

  // pending | granting | timeout — full warning banner with action button.
  const isGranting = phase === 'granting';
  const isTimeout = phase === 'timeout';
  const currentOrgName = getOrgName(orgId);

  return (
    <>
      <Alert
        severity="warning"
        variant="outline"
        mt="4x"
        isClosable
        onClose={handleDismiss}
      >
        {/*
          Single horizontal row: copy on the left, action button anchored to
          the right edge of the alert content, mirroring the design spec.
          align="flex-start" keeps the button vertically aligned with the
          first line of body text even when the copy wraps to multiple
          lines. flex="1" + minWidth={0} on the text column lets the copy
          wrap properly without pushing the button off the right edge, and
          flexShrink={0} on the action column protects the button from
          getting squeezed when the body text is long.
        */}
        <Flex direction="row" align="flex-start" gap="4x">
          <Flex direction="column" gap="2x" flex="1" minWidth={0}>
            <Text>
              {t('globalSetting.misdirectedEmailDetection.consentBanner.title')}
              :{' '}
              {t('globalSetting.misdirectedEmailDetection.consentBanner.body')}
            </Text>
            {isGranting && (
              <Text>
                {t(
                  'globalSetting.misdirectedEmailDetection.consentBanner.grantingHint'
                )}
              </Text>
            )}
            {isTimeout && (
              <Text>
                {t(
                  'globalSetting.misdirectedEmailDetection.consentBanner.recheckHint'
                )}
              </Text>
            )}
          </Flex>
          <Flex flexShrink={0}>
            {isTimeout ? (
              <Button variant="secondary" onClick={handleRecheck}>
                {t(
                  'globalSetting.misdirectedEmailDetection.consentBanner.recheckButton'
                )}
              </Button>
            ) : (
              <Button
                variant="secondary"
                disabled={isGranting}
                onClick={openGrantWarning}
              >
                {isGranting && <Spinner size="sm" mr="2x" />}
                {t(
                  isGranting
                    ? 'globalSetting.misdirectedEmailDetection.consentBanner.grantingButton'
                    : 'globalSetting.misdirectedEmailDetection.consentBanner.grantButton'
                )}
              </Button>
            )}
          </Flex>
        </Flex>
      </Alert>
      <Modal
        autoFocus={false}
        closeOnEsc={true}
        closeOnOutsideClick={false}
        isClosable={true}
        isOpen={isGrantWarningOpen}
        onClose={closeGrantWarning}
        size="md"
      >
        <ModalContent width={560}>
          <ModalHeader>
            {t(
              'globalSetting.misdirectedEmailDetection.adminConsentWarning.title'
            )}
          </ModalHeader>
          <ModalBody>
            <Text mb="4x">
              {t(
                'globalSetting.misdirectedEmailDetection.adminConsentWarning.body'
              )}
            </Text>
            <Alert severity="warning" variant="outline" mb="4x">
              <Text>
                {t(
                  'globalSetting.misdirectedEmailDetection.adminConsentWarning.warning'
                )}
              </Text>
            </Alert>
            <Text fontWeight="semibold">
              {t(
                'globalSetting.misdirectedEmailDetection.adminConsentWarning.currentOrgLabel'
              )}
              : {currentOrgName}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={handleConfirmGrant}>
              {t(
                'globalSetting.misdirectedEmailDetection.adminConsentWarning.continueButton'
              )}
            </Button>
            <Space width="2x" />
            <Button onClick={closeGrantWarning}>
              {t(
                'globalSetting.misdirectedEmailDetection.adminConsentWarning.cancelButton'
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminConsentBanner;
